/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import * as Sentry from "@sentry/react"
import React from "react"
import { Alert, Button, Container } from "react-bootstrap"

interface ErrorBoundaryState {
  hasError: boolean
  eventId?: string
}

interface ErrorBoundaryProps {
  readonly children: React.ReactNode
  readonly fallback?: React.ComponentType<{ readonly error: Error; readonly eventId?: string }>
}

class SentryErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error("Error caught by boundary:", error, errorInfo)
    }

    Sentry.withScope(scope => {
      scope.setTag("errorBoundary", true)
      scope.setContext("errorInfo", {
        componentStack: errorInfo.componentStack,
      })
      const eventId = Sentry.captureException(error)
      this.setState({ eventId })
    })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback
        return (
          <FallbackComponent
            error={new Error("Error boundary triggered")}
            eventId={this.state.eventId}
          />
        )
      }

      return <DefaultErrorFallback eventId={this.state.eventId} />
    }

    return this.props.children
  }
}

const DefaultErrorFallback: React.FC<{ readonly eventId?: string }> = ({ eventId }) => {
  const handleReportFeedback = () => {
    if (eventId) {
      Sentry.showReportDialog({ eventId })
    }
  }

  const handleReload = () => {
    window.location.reload()
  }

  return (
    <Container className="mt-5">
      <Alert variant="danger">
        <Alert.Heading>Something went wrong</Alert.Heading>
        <p>
          We&apos;re sorry, but something unexpected happened. The error has been automatically
          reported to our team.
        </p>
        {eventId && (
          <p className="mb-0">
            <small className="text-muted">Error ID: {eventId}</small>
          </p>
        )}
        <hr />
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={handleReload}>
            Reload Page
          </Button>
          {eventId && (
            <Button variant="outline-secondary" onClick={handleReportFeedback}>
              Report Feedback
            </Button>
          )}
        </div>
      </Alert>
    </Container>
  )
}

export default SentryErrorBoundary
